import React from 'react';
import type {
    MenuItem as MenuItemType,
    MenuItemAction,
    MenuItemRadio,
    MenuItemCheckbox,
    MenuItemSubmenu,
    MenuItemCustom,
} from './types';

interface MenuItemProps {
    item: MenuItemAction | MenuItemRadio | MenuItemCheckbox | MenuItemSubmenu | MenuItemCustom;
    focused: boolean;
    tabIndex: number;
    onClick: () => void;
    hasOpenSubmenu?: boolean;
}

function getRoleForItem(item: MenuItemProps['item']): string {
    switch (item.type) {
        case 'radio':
            return 'menuitemradio';
        case 'checkbox':
            return 'menuitemcheckbox';
        default:
            return 'menuitem';
    }
}

function isChecked(item: MenuItemProps['item']): boolean {
    return (item.type === 'radio' || item.type === 'checkbox') && item.checked;
}

export const MenuItemComponent = React.memo(function MenuItemComponent({
    item,
    focused,
    tabIndex,
    onClick,
    hasOpenSubmenu,
}: MenuItemProps) {
    const role = getRoleForItem(item);
    const checked = isChecked(item);
    const disabled = item.disabled === true;

    const classNames = [
        'kollab-menu-item',
        checked ? 'kollab-menu-item--active' : '',
        disabled ? 'kollab-menu-item--disabled' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const handleClick = () => {
        if (!disabled) {
            onClick();
        }
    };

    const ariaProps: Record<string, string | boolean | undefined> = {};
    if (item.type === 'radio' || item.type === 'checkbox') {
        ariaProps['aria-checked'] = item.checked;
    }
    if (disabled) {
        ariaProps['aria-disabled'] = 'true';
    }
    if (item.type === 'submenu') {
        ariaProps['aria-haspopup'] = 'menu';
        ariaProps['aria-expanded'] = hasOpenSubmenu ?? false;
    }

    // Render icon if present
    const icon = 'icon' in item && item.icon ? (
        <span className="kollab-menu-item-icon">{item.icon}</span>
    ) : null;

    // Render hint if present
    const hint = 'hint' in item && (item as MenuItemAction).hint ? (
        <span className="kollab-menu-item-hint">{(item as MenuItemAction).hint}</span>
    ) : null;

    // Render submenu arrow
    const arrow = item.type === 'submenu' ? (<>
        <span className="spacer" />
        <span className="kollab-menu-item-arrow" />
    </>) : null;

    // Render label
    const label = item.type === 'custom' ? item.render({ focused }) : (
        <>
            {icon}
            <span className="kollab-menu-item-label">{'label' in item ? item.label : null}</span>
            {hint}
            {arrow}
        </>
    );

    return (
        <div
            role={role}
            className={classNames}
            tabIndex={tabIndex}
            data-menu-item=""
            data-menu-item-id={item.id}
            onClick={handleClick}
            {...ariaProps}
        >
            {label}
        </div>
    );
});
